import React, { useState } from 'react';
import type { ResearchProgressState } from '../../types/research';
import { formatCount } from '../../utils/plural';

interface DeepResearchProgressProps {
  researchProgress: ResearchProgressState;
}

const DeepResearchProgress: React.FC<DeepResearchProgressProps> = ({ researchProgress }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { isActive, phases = [], findings = [], coverage, gaps, premiseCorrections, totalPremises } = researchProgress || {};

  const activePhase = phases?.find(p => p.status === 'active');
  const completedCount = phases?.filter(p => p.status === 'completed').length || 0;
  const isComplete = !isActive && completedCount > 0;

  return (
    <div className="relative flex items-start">
      {/* Blue dot marker — pulsing when active, green when complete */}
      <div
        className={`relative z-10 w-[10px] h-[10px] rounded-full border border-white shadow-sm flex-shrink-0 mt-0.5 ml-1 ${
          isComplete
            ? 'bg-success'
            : isActive
              ? 'bg-accent animate-pulse'
              : 'bg-accent'
        }`}
      />

      <div className="flex-1 ml-3">
        {/* Header — always visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-xs text-fg-secondary hover:text-fg transition-colors py-0.5"
        >
          <svg
            className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-medium text-fg-secondary">Deep research</span>
          {activePhase && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-accent-tint text-accent-hover">
              {activePhase.label}
            </span>
          )}
          {isComplete && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-success-bg text-success">
              Complete
            </span>
          )}
          {findings.length > 0 && (
            <span className="text-fg-tertiary">
              {formatCount(findings.length, { one: 'finding', other: 'findings' })}
            </span>
          )}
          {isActive && (
            <span className="inline-block w-1.5 h-3 bg-accent animate-pulse ml-1" />
          )}
        </button>

        {/* Expanded content */}
        {isExpanded && phases.length > 0 && (
          <div className="mt-2 ml-5 pl-3 border-l border-border-default bg-bg-warm rounded-r py-2 pr-3">
            {/* Phase timeline with detail lines */}
            <div className="space-y-0.5 mb-2">
              {phases.map((phase) => (
                <div key={phase.name}>
                  <div className="flex items-center gap-2 text-xs">
                    {phase.status === 'completed' && (
                      <span className="text-success w-3 text-center font-medium">&#10003;</span>
                    )}
                    {phase.status === 'active' && (
                      <span className="inline-block w-3 text-center">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-bounce" />
                      </span>
                    )}
                    {phase.status === 'pending' && (
                      <span className="text-fg-tertiary w-3 text-center">&#9675;</span>
                    )}
                    <span
                      className={
                        phase.status === 'completed'
                          ? 'text-fg-tertiary'
                          : phase.status === 'active'
                            ? 'text-fg-secondary font-medium'
                            : 'text-fg-tertiary'
                      }
                    >
                      {phase.label}
                    </span>
                  </div>
                  {/* Detail lines under each phase */}
                  {phase.details && phase.details.length > 0 && (
                    <div className="ml-5 pl-2 border-l border-divider mt-0.5 mb-1">
                      {phase.details.map((detail, i) => (
                        <div key={i} className="text-xs text-fg-tertiary leading-relaxed">
                          {detail}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Coverage bar */}
            {coverage && (
              <div className="mt-2 pt-2 border-t border-border-default">
                <div className="flex items-center gap-2 text-xs text-fg-tertiary mb-1">
                  <span>
                    {formatCount(coverage.findingsCount, { one: 'finding', other: 'findings' })}
                    {' across '}
                    {formatCount(coverage.layersCovered.length, { one: 'layer', other: 'layers' })}
                  </span>
                </div>
                <div className="bg-bg-warm rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-accent h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(
                        100,
                        (coverage.layersCovered.length /
                          Math.max(1, coverage.layersCovered.length + coverage.layersMissing.length)) *
                          100
                      )}%`,
                    }}
                  />
                </div>
                {coverage.layersMissing.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {coverage.layersMissing.map((layer) => (
                      <span
                        key={layer}
                        className="text-xs px-1.5 py-0.5 rounded bg-warning-bg text-warning"
                      >
                        {layer} missing
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Latest findings */}
            {findings.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border-default">
                <div className="text-xs text-fg-tertiary mb-1">
                  Latest findings ({findings.length})
                </div>
                <div className="space-y-0.5">
                  {findings.slice(-3).map((finding) => (
                    <div
                      key={finding.id}
                      className="text-xs text-fg-secondary flex items-start gap-1"
                    >
                      <span className="text-success flex-shrink-0">+</span>
                      <span className="line-clamp-1">{finding.summary}</span>
                    </div>
                  ))}
                  {findings.length > 3 && (
                    <div className="text-xs text-fg-tertiary">
                      ...and {findings.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Gap alert */}
            {gaps && gaps.gapCount > 0 && (
              <div className="mt-2 pt-2 border-t border-border-default">
                <div className="text-xs text-warning">
                  {gaps.gapCount} coverage gaps identified
                  {gaps.hasActionableGaps && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded bg-warning-bg text-warning">
                      Spawning wave 2
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Premise corrections */}
            {premiseCorrections > 0 && (
              <div className="mt-2 pt-2 border-t border-border-default">
                <div className="text-xs text-node-definition">
                  {premiseCorrections} premise corrections (of {totalPremises} checked)
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeepResearchProgress;
